<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, string, TestContext } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DCard,
  DBtn,
  DTextField,
  DMultiselect,
  DToggleField,
  DModal,
  DLoading
} from 'front-library'
import {
  ReportSalaryHistoric,
  generateSalaryHistoric,
  getPayroll,
  getDepartments,
  getIncreaseReasons,
  getBaseSalaries,
  getReportRequest
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const itemsPayrolls = ref<any[]>([])
const itemsDepartments = ref<any[]>([])
const itemsIncreaseReasons = ref<any[]>([])
const itemsBaseSalaries = ref<any[]>([])
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validateEntryDateFrom = function (value: any, context: TestContext) {
  if (entryDateUntil.value) {
    if (value === '') {
      throw context.createError({
        path: 'entryDateFrom',
        message: t('form.valid.messageDateFromRequired')
      })
    } else {
      return true
    }
  }
  return true
}
const validateEntryDateUntil = function (value: any, context: TestContext) {
  if (entryDateFrom.value) {
    if (value === '') {
      throw context.createError({
        path: 'entryDateUntil',
        message: t('form.valid.messageDateToRequired')
      })
    } else if (new Date(value) < new Date(entryDateFrom.value)) {
      throw context.createError({
        path: 'entryDateUntil',
        message: t('form.valid.messageDateToValidation')
      })
    } else {
      return true
    }
  }
  return true
}
const validateSalaryDateFrom = function (value: any, context: TestContext) {
  if (salaryMovementDateUntil.value) {
    if (value === '') {
      throw context.createError({
        path: 'salaryMovementDateFrom',
        message: t('form.valid.messageSalaryMovementFromRequired')
      })
    } else {
      return true
    }
  }
  return true
}
const validateSalaryDateUntil = function (value: any, context: TestContext) {
  if (salaryMovementDateFrom.value) {
    if (value === '') {
      throw context.createError({
        path: 'salaryMovementDateUntil',
        message: t('form.valid.messageSalaryMovementToRequired')
      })
    } else if (new Date(value) < new Date(salaryMovementDateFrom.value)) {
      throw context.createError({
        path: 'salaryMovementDateUntil',
        message: t('form.valid.messageSalaryMovementToValidation')
      })
    } else {
      return true
    }
  }
  return true
}
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  entryDateFrom: string().test('entryDateFrom', '', validateEntryDateFrom),
  entryDateUntil: string().test('entryDateUntil', '', validateEntryDateUntil),
  salaryMovementDateFrom: string().test(
    'salaryMovementDateFrom',
    '',
    validateSalaryDateFrom
  ),
  salaryMovementDateUntil: string().test(
    'salaryMovementDateUntil',
    '',
    validateSalaryDateUntil
  )
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: { includeCeasedEmployees: false },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: entryDateFrom } = useField<string>('entryDateFrom')
const { value: entryDateUntil } = useField<string>('entryDateUntil')
const { value: departmentsId } = useField<string[]>('filters.departmentsId')
const { value: causeOfSalaryIncreasesId } = useField<string[]>(
  'filters.causeOfSalaryIncreasesId'
)
const { value: baseOfSalariesId } = useField<string[]>(
  'filters.baseOfSalariesId'
)
const { value: salaryMovementDateFrom } = useField<string>(
  'salaryMovementDateFrom'
)
const { value: salaryMovementDateUntil } = useField<string>(
  'salaryMovementDateUntil'
)
const { value: includeCeasedEmployees } = useField<boolean>(
  'filters.includeCeasedEmployees'
)

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportSalaryHistoric = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateSalaryHistoric(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null

  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar departamentos */
const listDepartment = async () => {
  itemsDepartments.value = []
  if (payrolls.value.length > 0) {
    const companiesId: any = [
      ...new Set(
        itemsPayrolls.value
          .filter(item => payrolls.value.includes(item.id))
          .map(item => item.companyId)
      )
    ]
    itemsDepartments.value = await getDepartments(companiesId)
  }
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    itemsPayrolls.value = await getPayroll(store.user?.id as string)
    payrolls.value = itemsPayrolls.value
      .filter((_: any) => _.id === storePayroll.payroll?.id)
      .map((_: any) => _.id)
    listDepartment()
    itemsIncreaseReasons.value = await getIncreaseReasons()
    itemsBaseSalaries.value = await getBaseSalaries()
    entryDateFrom.value = ''
    entryDateUntil.value = ''
    salaryMovementDateFrom.value = ''
    salaryMovementDateUntil.value = ''
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.historicalSalary')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-8 gap-4">
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="listDepartment()" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.entryDateFrom')"
                    v-model="entryDateFrom"
                    :error="errors.entryDateFrom" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.entryDateUntil')"
                    v-model="entryDateUntil"
                    :error="errors.entryDateUntil" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.departments')"
                    v-model="departmentsId"
                    :headerTitle="t('reports.fields.departments')"
                    :options="itemsDepartments" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.causesIncrease')"
                    v-model="causeOfSalaryIncreasesId"
                    :headerTitle="t('reports.fields.causesIncrease')"
                    :options="itemsIncreaseReasons" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.baseSalary')"
                    v-model="baseOfSalariesId"
                    :headerTitle="t('reports.fields.baseSalary')"
                    :options="itemsBaseSalaries" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.salaryMovementDateFrom')"
                    v-model="salaryMovementDateFrom"
                    :error="errors.salaryMovementDateFrom" />
                </div>
                <div class="col-span-6 sm:col-span-4 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.salaryMovementDateUntil')"
                    v-model="salaryMovementDateUntil"
                    :error="errors.salaryMovementDateUntil" />
                </div>
                <div
                  class="col-span-6 sm:col-span-4 lg:col-span-2 flex justify-start items-center">
                  <DToggleField
                    :label="t('reports.fields.includeTerminatedEmployees')"
                    v-model="includeCeasedEmployees" />
                </div>
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
